import { ref, readonly, computed } from "vue";
import { useSdk, getSdk } from "./useSdk";
import type {
  AppSettings,
  AppReaction,
  AppBadge,
  AppGenre,
  SiteSettingsUI,
  NotificationType,
  AppViolation,
} from "@cc-consulting-nv/ccsdk";

// Reactive state (singleton)
const isLoaded = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
const lastLoadedAt = ref<number>(0);

// Settings data
// Default to empty - will be set from API response
const siteType = ref<string>("");
const siteLogo = ref<string | null>(null);
const siteFavicon = ref<string | null>(null);
const siteWebsite = ref<string | null>(null);
const companyName = ref<string>("Closed Circuit Inc.");
const uiMode = ref<"light" | "dark" | "both">("both");
const uiSettings = ref<SiteSettingsUI>({});
// Legacy alias for backward compatibility
const uiColors = uiSettings;
const featureMessagingEnabled = ref(true);

// Content data
const reactions = ref<AppReaction[]>([]);
const badges = ref<AppBadge[]>([]);
const genres = ref<AppGenre[]>([]);

// Notification types from /v1/settings/notification-types (name -> description template like "%s followed you")
const notificationTypes = ref<NotificationType[]>([]);
const notificationTypesLoaded = ref(false);

// Cache duration: 15 minutes
const CACHE_DURATION = 15 * 60 * 1000;

/**
 * Check if site is music-only mode (can be used outside Vue setup context)
 * Safe to call from router guards, doesn't instantiate SDK
 * Returns true only for "music" site type, not "music,social"
 * Falls back to localStorage if settings haven't loaded yet
 * Defaults to true (music mode) if no setting is available yet
 */
export function isMusicModeEnabled(): boolean {
  const refValue = siteType.value;
  const localValue = localStorage.getItem("app_site_type");
  const type = refValue || localValue || "";

  console.log("[isMusicModeEnabled] siteType.value:", refValue, "localStorage:", localValue, "resolved type:", type);

  // If no type set yet, default to music mode (will redirect to /explore)
  // Once settings load, the correct behavior will take over
  if (!type) {
    console.log("[isMusicModeEnabled] No type set, defaulting to music mode");
    return true;
  }

  const result = type === "music";
  console.log("[isMusicModeEnabled] type === 'music':", result);
  return result;
}

/**
 * Async version that fetches settings if not loaded yet, then determines music mode.
 * Use this in router guards to ensure correct behavior on first load.
 */
export async function isMusicModeEnabledAsync(): Promise<boolean> {
  // If settings already loaded (ref has value), use it directly
  if (siteType.value) {
    return siteType.value === "music";
  }

  // If localStorage has a cached value, use it while we fetch fresh
  const cachedType = localStorage.getItem("app_site_type");
  if (cachedType) {
    return cachedType === "music";
  }

  // No cached value - fetch settings via SDK (goes through Vite proxy)
  try {
    const response = await getSdk().getGlobalSettings();
    const fetchedType = response?.data?.siteSettings?.ui?.siteType || "";
    if (fetchedType) {
      localStorage.setItem("app_site_type", fetchedType);
      siteType.value = fetchedType;
      return fetchedType === "music";
    }
  } catch (err) {
    console.warn("[isMusicModeEnabledAsync] Failed to fetch settings:", err);
  }

  // Fallback to music mode if fetch fails
  return true;
}

/**
 * Composable to load and access app settings from /v1/settings
 */
export function useAppSettings() {
  const { sdk } = useSdk();

  // Computed: reaction map for quick lookup
  const reactionMap = computed(() => {
    const map: Record<string, { emoji?: string; label?: string }> = {};
    reactions.value.forEach((r) => {
      if (r.name) {
        map[r.name] = {
          emoji: r.value,
          label: r.description || r.name,
        };
      }
    });
    return map;
  });

  // Computed: notification type map for quick lookup (name -> description template)
  const notificationTypeMap = computed(() => {
    const map: Record<string, string> = {};
    notificationTypes.value.forEach((t: NotificationType) => {
      if (t.name && t.description) {
        map[t.name] = t.description;
      }
    });
    return map;
  });

  // Computed: genre tree (top-level genres)
  const topLevelGenres = computed(() => {
    return genres.value.filter((g) => !g.parentId);
  });

  // Computed: is music mode
  const isMusicMode = computed(() => {
    return siteType.value === "music" || siteType.value.includes("music");
  });

  // Computed: is social mode
  const isSocialMode = computed(() => {
    return siteType.value === "social" || siteType.value.includes("social");
  });

  /**
   * Load settings from API (with caching)
   */
  async function loadSettings(force = false): Promise<void> {
    // Skip if already loaded and not forcing
    if (isLoaded.value && !force) {
      const now = Date.now();
      if (now - lastLoadedAt.value < CACHE_DURATION) {
        return;
      }
    }

    // Skip if already loading
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await sdk.getGlobalSettings();
      const settings: AppSettings = response.data;

      // Extract site settings
      const ui = settings.siteSettings?.ui ?? {};

      // Site type (music, social, or music,social)
      // API returns camelCase: siteType (from ui-site-type tenant setting)
      if (ui.siteType) {
        console.log("[useAppSettings] Setting siteType:", ui.siteType);
        siteType.value = ui.siteType;
        localStorage.setItem("app_site_type", ui.siteType);
      } else {
        console.log("[useAppSettings] No siteType in API response, ui:", ui);
      }

      // Site logo - prefer 192px for header (optimal for 36px display at 3x retina)
      // Fallback chain: logo192 -> logo -> legacy siteLogo
      if (settings.siteSettings?.logos?.logo192) {
        siteLogo.value = settings.siteSettings.logos.logo192;
      } else if (settings.siteSettings?.logos?.logo) {
        siteLogo.value = settings.siteSettings.logos.logo;
      } else if (settings.siteSettings?.siteLogo) {
        siteLogo.value = settings.siteSettings.siteLogo;
      }

      // Site favicon - update document favicon if tenant has custom one
      if (settings.siteSettings?.logos?.favicon) {
        siteFavicon.value = settings.siteSettings.logos.favicon;
        // Update the favicon link element in document head
        const faviconEl = document.getElementById("favicon") as HTMLLinkElement;
        if (faviconEl) {
          const faviconUrl = settings.siteSettings.logos.favicon;
          faviconEl.href = faviconUrl;
          // Set correct MIME type based on extension
          const isIco = faviconUrl.toLowerCase().endsWith(".ico");
          faviconEl.type = isIco ? "image/x-icon" : "image/png";
        }
      }

      // Company name (from ui-company-name tenant setting)
      if (ui.companyName) {
        companyName.value = ui.companyName;
      }

      // Website URL
      if (ui.website) {
        siteWebsite.value = ui.website;
      }

      // Feature flags from backend (not yet typed in ccsdk)
      const features = (settings as AppSettings & { features?: { messaging?: boolean } }).features;
      if (features) {
        featureMessagingEnabled.value = features.messaging ?? true;
      }

      // UI mode
      if (ui.mode && ["light", "dark", "both"].includes(ui.mode)) {
        uiMode.value = ui.mode as "light" | "dark" | "both";
      }

      // Store all UI settings (includes all colors and config)
      uiSettings.value = { ...ui };
      console.log("[useAppSettings] Stored uiSettings:", {
        keys: Object.keys(ui),
        primaryColorDark: ui.primaryColorDark,
        mode: ui.mode,
      });

      // Reactions
      if (settings.reactions && settings.reactions.length > 0) {
        reactions.value = settings.reactions;
      }

      // Badges
      if (settings.badges && settings.badges.length > 0) {
        badges.value = settings.badges;
      }

      // Genres
      if (settings.genres && settings.genres.length > 0) {
        genres.value = settings.genres;
      }

      // User notification types from /v1/settings may be present (legacy)
      // but we prefer the dedicated endpoint loaded separately
      if (
        settings.userNotificationTypes &&
        settings.userNotificationTypes.length > 0 &&
        !notificationTypesLoaded.value
      ) {
        notificationTypes.value = settings.userNotificationTypes;
      }

      isLoaded.value = true;
      lastLoadedAt.value = Date.now();

      // Also load notification types from the dedicated endpoint
      loadNotificationTypes();
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to load settings";
      console.error("Failed to load app settings:", err);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Load notification types from dedicated /v1/settings/notification-types endpoint
   */
  async function loadNotificationTypes(force = false): Promise<void> {
    if (notificationTypesLoaded.value && !force) {
      return;
    }

    try {
      // TODO: Backend route /v1/settings/notification-types not yet implemented
      // SDK method exists, but backend endpoint is missing from OpenAPI spec
      // Uncomment when backend route is added
      // const response = await sdk.getNotificationTypes();
      // if (
      //   response.data &&
      //   Array.isArray(response.data) &&
      //   response.data.length > 0
      // ) {
      //   notificationTypes.value = response.data;
      //   console.log(
      //     "[useAppSettings] Loaded notification types from /v1/settings/notification-types:",
      //     response.data.length,
      //   );
      // }
      notificationTypesLoaded.value = true;
    } catch (err) {
      // Non-critical - we may already have types from /v1/settings
      console.warn(
        "Failed to load notification types from dedicated endpoint:",
        err,
      );
    }
  }

  /**
   * Invalidate cache and force reload on next access
   */
  function invalidateCache(): void {
    lastLoadedAt.value = 0;
    isLoaded.value = false;
    notificationTypesLoaded.value = false;
  }

  /**
   * Get reaction emoji by name
   */
  function getReactionEmoji(name: string): string | undefined {
    return reactionMap.value[name]?.emoji;
  }

  /**
   * Get genre by ID
   */
  function getGenreById(id: number): AppGenre | undefined {
    return genres.value.find((g) => g.id === id);
  }

  /**
   * Get genre by slug
   */
  function getGenreBySlug(slug: string): AppGenre | undefined {
    return genres.value.find((g) => g.slug === slug);
  }

  /**
   * Get child genres for a parent
   */
  function getChildGenres(parentId: number): AppGenre[] {
    return genres.value.filter((g) => g.parentId === parentId);
  }

  /**
   * Get notification description template by type name
   * Returns the template string like "%s followed you" or undefined if not found
   */
  function getNotificationDescription(typeName: string): string | undefined {
    return notificationTypeMap.value[typeName];
  }

  /**
   * Format notification text using the template and actor name
   * Example: getFormattedNotificationText('follow', 'John') => 'John followed you'
   */
  function getFormattedNotificationText(
    typeName: string,
    actorName: string,
  ): string | undefined {
    const template = notificationTypeMap.value[typeName];
    if (!template) return undefined;
    // Replace %s placeholder with actor name
    return template.replace("%s", actorName);
  }

  return {
    // State (readonly)
    isLoaded: readonly(isLoaded),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Site settings (readonly)
    siteType: readonly(siteType),
    siteLogo: readonly(siteLogo),
    siteFavicon: readonly(siteFavicon),
    siteWebsite: readonly(siteWebsite),
    companyName: readonly(companyName),
    uiMode: readonly(uiMode),
    uiSettings: readonly(uiSettings),
    uiColors: readonly(uiColors), // Legacy alias
    featureMessagingEnabled: readonly(featureMessagingEnabled),

    // Content data (readonly)
    reactions: readonly(reactions),
    badges: readonly(badges),
    genres: readonly(genres),
    notificationTypes: readonly(notificationTypes),

    // Computed
    reactionMap,
    notificationTypeMap,
    topLevelGenres,
    isMusicMode,
    isSocialMode,

    // Actions
    loadSettings,
    loadNotificationTypes,
    invalidateCache,

    // Helpers
    getReactionEmoji,
    getGenreById,
    getGenreBySlug,
    getChildGenres,
    getNotificationDescription,
    getFormattedNotificationText,
  };
}

// Export type
export type UseAppSettingsReturn = ReturnType<typeof useAppSettings>;
